import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { User, MapPin, Briefcase, Save, Phone, Mail, Globe, Linkedin, Github, FileText, Upload, Plus, Trash2, GraduationCap, Loader2, Eye } from "lucide-react";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEmployer } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyExp = { title: "", company: "", start: "", end: "", description: "" };
const emptyEdu = { school: "", degree: "", year: "" };

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) setForm({
      name: user.name || "", headline: user.headline || "", bio: user.bio || "",
      location: user.location || "", skills: (user.skills || []).join(", "),
      phone: user.phone || "", contact_email: user.contact_email || "", website: user.website || "",
      linkedin: user.linkedin || "", github: user.github || "",
      experiences: user.experiences?.length ? user.experiences : [{ ...emptyExp }],
      education: user.education?.length ? user.education : [{ ...emptyEdu }],
      resume_url: user.resume_url || "", resume_name: user.resume_name || "",
    });
  }, [user]);

  if (!form || !user) return (<div className="min-h-screen wo-bg"><Navbar /></div>);

  const employer = isEmployer(user.role);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setExp = (i, k, v) => setForm((f) => ({ ...f, experiences: f.experiences.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
  const setEdu = (i, k, v) => setForm((f) => ({ ...f, education: f.education.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, headline: form.headline, bio: form.bio, location: form.location,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        phone: form.phone, contact_email: form.contact_email, website: form.website,
        linkedin: form.linkedin, github: form.github,
        experiences: form.experiences.filter((e) => e.title || e.company),
        education: form.education.filter((e) => e.school || e.degree),
      };
      const { data } = await api.put("/profile", payload);
      setUser(data);
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success(data.parsed ? "Résumé uploaded — profile auto-filled from your CV" : "Résumé uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const viewResume = () => {
    const token = localStorage.getItem("wo_token");
    const path = form.resume_url.replace(/^\/api\/files\//, "");
    window.open(`${BACKEND_URL}/api/files/${path}?auth=${token}`, "_blank");
  };

  const field = (label, key, Icon, placeholder, type = "text") => (
    <div>
      <Label className="text-slate-300 text-sm flex items-center gap-1.5"><Icon size={14} /> {label}</Label>
      <Input data-testid={`profile-${key}`} type={type} value={form[key]} onChange={(e) => set(key, e.target.value)} placeholder={placeholder}
        className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100" />
    </div>
  );

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8 space-y-6">
        {/* header */}
        <div className="glass rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-400 to-blue-600 flex items-center justify-center text-2xl font-display font-bold text-slate-900">
              {form.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div>
              <h1 className="font-display font-bold text-2xl text-slate-100">{form.name || "Your profile"}</h1>
              <p className="text-slate-400 text-sm flex items-center gap-2 mt-1">
                <span className="capitalize px-2 py-0.5 rounded-full bg-teal-500/10 text-teal-300 border border-teal-500/20 text-xs">{user.role?.replace("_", " ")}</span>
                {user.email}
              </p>
            </div>
          </div>
        </div>

        {/* résumé (job seekers only) */}
        {!employer && (
        <div className="glass rounded-2xl p-6 sm:p-8">
          <h2 className="font-display font-semibold text-slate-200 mb-4 flex items-center gap-2"><FileText size={18} className="text-teal-400" /> Résumé</h2>
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt" onChange={onUpload} className="hidden" data-testid="resume-file-input" />
          {form.resume_url ? (
            <div className="flex items-center justify-between rounded-xl bg-slate-950/50 border border-teal-500/20 p-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400"><FileText size={18} /></div>
                <span className="text-slate-200 text-sm truncate">{form.resume_name}</span>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" onClick={viewResume} data-testid="profile-view-resume" className="border-slate-700 text-slate-200"><Eye size={15} /></Button>
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-slate-800 text-slate-200 hover:bg-slate-700">
                  {uploading ? <Loader2 size={15} className="animate-spin" /> : "Replace"}
                </Button>
              </div>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="resume-upload-button"
              className="w-full rounded-xl border-2 border-dashed border-slate-700 hover:border-teal-500/40 p-8 text-center transition-colors">
              {uploading ? <Loader2 size={22} className="mx-auto text-teal-400 animate-spin" /> : <Upload size={22} className="mx-auto text-slate-500 mb-2" />}
              <div className="text-sm text-slate-300 mt-2">Upload your résumé</div>
              <div className="text-xs text-slate-500">PDF, DOC, DOCX or TXT · max 8MB</div>
            </button>
          )}
        </div>
        )}

        {/* basics */}
        <div className="glass rounded-2xl p-6 sm:p-8 space-y-5">
          <h2 className="font-display font-semibold text-slate-200 flex items-center gap-2"><User size={18} className="text-teal-400" /> {employer ? "Company information" : "Basic info"}</h2>
          {field(employer ? "Company name" : "Full name", "name", User, employer ? "Acme Inc." : "Jane Doe")}
          {field(employer ? "Tagline" : "Headline", "headline", Briefcase, employer ? "e.g. We build the future of X" : "e.g. Senior Frontend Engineer")}
          {field("Location", "location", MapPin, "e.g. San Francisco, CA")}
          <div>
            <Label className="text-slate-300 text-sm">{employer ? "About the company" : "Bio"}</Label>
            <Textarea data-testid="profile-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder={employer ? "Tell candidates about your company..." : "Tell employers about yourself..."} className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100 min-h-[90px]" />
          </div>
          {!employer && (
          <div>
            <Label className="text-slate-300 text-sm">Skills (comma separated)</Label>
            <Input data-testid="profile-skills" value={form.skills} onChange={(e) => set("skills", e.target.value)} placeholder="React, TypeScript, Node.js" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100" />
          </div>
          )}
        </div>

        {/* contact */}
        <div className="glass rounded-2xl p-6 sm:p-8 space-y-5">
          <h2 className="font-display font-semibold text-slate-200 flex items-center gap-2"><Phone size={18} className="text-teal-400" /> Contact & links</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Mobile phone", "phone", Phone, "+1 555 000 1234", "tel")}
            {field("Contact email", "contact_email", Mail, "you@example.com", "email")}
            {field("Website", "website", Globe, "https://...")}
            {field("LinkedIn", "linkedin", Linkedin, "linkedin.com/in/...")}
            {field("GitHub", "github", Github, "github.com/...")}
          </div>
        </div>

        {/* experience */}
        {!employer && (
        <div className="glass rounded-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-semibold text-slate-200 flex items-center gap-2"><Briefcase size={18} className="text-teal-400" /> Experience</h2>
            <Button size="sm" onClick={() => set("experiences", [...form.experiences, { ...emptyExp }])} data-testid="add-experience" className="bg-slate-800 text-slate-200 hover:bg-slate-700"><Plus size={14} className="mr-1" /> Add</Button>
          </div>
          <div className="space-y-4">
            {form.experiences.map((e, i) => (
              <div key={i} className="rounded-xl bg-slate-950/40 border border-slate-800 p-4 space-y-3" data-testid="experience-item">
                <div className="flex justify-between gap-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                    <Input value={e.title} onChange={(ev) => setExp(i, "title", ev.target.value)} placeholder="Role title" className="bg-slate-950/50 border-slate-700 text-slate-100" />
                    <Input value={e.company} onChange={(ev) => setExp(i, "company", ev.target.value)} placeholder="Company" className="bg-slate-950/50 border-slate-700 text-slate-100" />
                    <Input value={e.start} onChange={(ev) => setExp(i, "start", ev.target.value)} placeholder="Start (e.g. Jan 2022)" className="bg-slate-950/50 border-slate-700 text-slate-100" />
                    <Input value={e.end} onChange={(ev) => setExp(i, "end", ev.target.value)} placeholder="End (or Present)" className="bg-slate-950/50 border-slate-700 text-slate-100" />
                  </div>
                  {form.experiences.length > 1 && (
                    <button onClick={() => set("experiences", form.experiences.filter((_, idx) => idx !== i))} className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 size={16} /></button>
                  )}
                </div>
                <Textarea value={e.description} onChange={(ev) => setExp(i, "description", ev.target.value)} placeholder="What did you do?" className="bg-slate-950/50 border-slate-700 text-slate-100 min-h-[60px]" />
              </div>
            ))}
          </div>
        </div>
        )}

        {/* education */}
        {!employer && (
        <div className="glass rounded-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-semibold text-slate-200 flex items-center gap-2"><GraduationCap size={18} className="text-teal-400" /> Education</h2>
            <Button size="sm" onClick={() => set("education", [...form.education, { ...emptyEdu }])} data-testid="add-education" className="bg-slate-800 text-slate-200 hover:bg-slate-700"><Plus size={14} className="mr-1" /> Add</Button>
          </div>
          <div className="space-y-3">
            {form.education.map((e, i) => (
              <div key={i} className="rounded-xl bg-slate-950/40 border border-slate-800 p-4 flex gap-2" data-testid="education-item">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
                  <Input value={e.school} onChange={(ev) => setEdu(i, "school", ev.target.value)} placeholder="School" className="bg-slate-950/50 border-slate-700 text-slate-100" />
                  <Input value={e.degree} onChange={(ev) => setEdu(i, "degree", ev.target.value)} placeholder="Degree" className="bg-slate-950/50 border-slate-700 text-slate-100" />
                  <Input value={e.year} onChange={(ev) => setEdu(i, "year", ev.target.value)} placeholder="Year" className="bg-slate-950/50 border-slate-700 text-slate-100" />
                </div>
                {form.education.length > 1 && (
                  <button onClick={() => set("education", form.education.filter((_, idx) => idx !== i))} className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 size={16} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
        )}

        <div className="sticky bottom-4">
          <Button data-testid="profile-save-button" onClick={save} disabled={saving}
            className="w-full bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90 py-6 shadow-lg shadow-teal-500/20">
            <Save size={16} className="mr-2" /> {saving ? "Saving..." : "Save profile"}
          </Button>
        </div>
      </div>
    </div>
  );
}
